import { ArrowRightIcon } from "vega-icons";

import { type CollaboratorPermissionEntry } from "@vega-api/generated/types/collaboratorPermissionEntry";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Tag } from "@vega-core/components/Tag/Tag";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import {
  type EditedPermission,
  FUND_VEHICLE_ORDER,
  type FundVehicleTab,
  fundVehicleTagConfig,
  getFundVehicleTabFromPermission,
  type PendingRoleCreation,
} from "./collaboratorsV2Constants";

type ChangeStatus = "created" | "removed" | "updated";

const STATUS_ORDER: Record<ChangeStatus, number> = {
  created: 0,
  updated: 1,
  removed: 2,
};
const statusLabel: Record<ChangeStatus, () => string> = {
  created: () => m.settings_collaborators_roleCreated(),
  removed: () => m.settings_collaborators_roleRemoved(),
  updated: () => m.settings_collaborators_roleUpdated(),
};

// ─── Role change row ──────────────────────────────────────────────────────────

type RoleChangeEntry = {
  key: string;
  fundVehicleTab: FundVehicleTab;
  roleName: string;
  status: ChangeStatus;
  oldRoleName?: string;
};

const statusClassName: Record<ChangeStatus, string> = {
  created: "text-text/positive",
  updated: "text-text/secondary",
  removed: "text-text/negative",
};

const RoleChangeRow = ({
  fundVehicleTab,
  roleName,
  status,
  oldRoleName,
}: Omit<RoleChangeEntry, "key">) => (
  <div className="flex items-center justify-between gap-4 px-3 py-2.5">
    <div className="flex items-center gap-3">
      <Tag variant={fundVehicleTagConfig[fundVehicleTab].variant}>
        {fundVehicleTagConfig[fundVehicleTab].label()}
      </Tag>
      {status === "updated" && oldRoleName ? (
        <>
          <span
            className="body-base-medium text-text/negative line-through"
            data-testid="role-change-old-role-name"
          >
            {oldRoleName}
          </span>
          <ArrowRightIcon className="w-4 h-4 text-text/secondary shrink-0" />
          <span
            className="body-base-medium"
            data-testid="role-change-role-name"
          >
            {roleName}
          </span>
        </>
      ) : (
        <span
          className={cnTwMerge(
            "body-base-medium",
            status === "removed" && "text-text/negative line-through",
          )}
          data-testid="role-change-role-name"
        >
          {roleName}
        </span>
      )}
    </div>
    <span
      className={cnTwMerge("body-small-semibold", statusClassName[status])}
      data-testid="role-change-status-label"
    >
      {statusLabel[status]()}
    </span>
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildChangeEntries = (
  pendingCreations: PendingRoleCreation[],
  editedPermissions: Record<string, EditedPermission>,
  permissionsToDelete: CollaboratorPermissionEntry[],
  getRoleNameById: (roleId: string) => string,
): RoleChangeEntry[] => {
  const entries: RoleChangeEntry[] = [
    ...pendingCreations.map(
      (creation): RoleChangeEntry => ({
        key: `created-${creation.roleId}`,
        fundVehicleTab: creation.fundVehicleTab,
        roleName: getRoleNameById(creation.roleId),
        status: "created",
      }),
    ),
    ...Object.entries(editedPermissions).map(
      ([originalRoleId, edited]): RoleChangeEntry => ({
        key: `updated-${originalRoleId}`,
        fundVehicleTab: edited.fundVehicleTab,
        roleName: getRoleNameById(edited.roleId),
        oldRoleName: getRoleNameById(originalRoleId),
        status: "updated",
      }),
    ),
    ...permissionsToDelete.map(
      (permission): RoleChangeEntry => ({
        key: `removed-${permission.roleId}`,
        fundVehicleTab: getFundVehicleTabFromPermission(permission),
        roleName: getRoleNameById(permission.roleId as string),
        status: "removed",
      }),
    ),
  ];

  return entries.sort(
    (a, b) =>
      FUND_VEHICLE_ORDER[a.fundVehicleTab] -
        FUND_VEHICLE_ORDER[b.fundVehicleTab] ||
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  );
};

const buildDescription = (
  createdCount: number,
  updatedCount: number,
  removedCount: number,
): string => {
  const parts: string[] = [];

  if (createdCount > 0) {
    parts.push(
      m.settings_collaborators_nRolesCreated({ count: String(createdCount) }),
    );
  }

  if (updatedCount > 0) {
    parts.push(
      m.settings_collaborators_nRolesUpdated({ count: String(updatedCount) }),
    );
  }

  if (removedCount > 0) {
    parts.push(
      m.settings_collaborators_nRolesRemoved({ count: String(removedCount) }),
    );
  }

  return parts.join(", ");
};

type SaveRoleChangesModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  permissionsToDelete: CollaboratorPermissionEntry[];
  pendingCreations: PendingRoleCreation[];
  editedPermissions: Record<string, EditedPermission>;
  getRoleNameById: (roleId: string) => string;
  onDiscard: () => void;
  onConfirm: () => void;
};

export const SaveRoleChangesModal = ({
  open,
  setOpen,
  permissionsToDelete,
  pendingCreations,
  editedPermissions,
  getRoleNameById,
  onDiscard,
  onConfirm,
}: SaveRoleChangesModalProps) => {
  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  const handleDiscard = () => {
    onDiscard();
    setOpen(false);
  };

  const entries = buildChangeEntries(
    pendingCreations,
    editedPermissions,
    permissionsToDelete,
    getRoleNameById,
  );

  const description = buildDescription(
    pendingCreations.length,
    Object.keys(editedPermissions).length,
    permissionsToDelete.length,
  );

  return (
    <ActionaryModal
      dataTestId="save-role-changes-modal"
      description={description}
      fluidWidth
      open={open}
      setOpen={setOpen}
      skipCloseGuards
      title={m.settings_collaborators_saveRoleChanges()}
    >
      <div className="min-w-[32rem] max-h-80 overflow-y-auto border border-border/secondary rounded-small divide-y divide-border/secondary">
        {entries.map((entry) => (
          <RoleChangeRow
            fundVehicleTab={entry.fundVehicleTab}
            key={entry.key}
            oldRoleName={entry.oldRoleName}
            roleName={entry.roleName}
            status={entry.status}
          />
        ))}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <ThemedButton
          className="text-text/negative"
          dataTestId="discard-role-changes-button"
          onClick={handleDiscard}
          type="button"
          variant="secondary"
        >
          {m.btn_discard()}
        </ThemedButton>
        <ThemedButton
          dataTestId="confirm-role-changes-button"
          onClick={handleConfirm}
          type="button"
        >
          {m.common_saveChanges()}
        </ThemedButton>
      </div>
    </ActionaryModal>
  );
};
